<template>
  <div class="d-flex justify-content-end">
    <McTableFilter
      ref="filterRef"
      :parseFilterDataMappings="parseFilterDataMappings"
      :activeFilters="activeFilters"
      :presetFullObjects="savedFullObjects"
      @submit="handleSubmit"
      @reset="handleReset"
    >
      <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
        <div class="mc-modal__filter-item" data-me="master-fr-modal-filter-license-plate">
          <FormulateInput
            v-bind="vehicleFields"
            :fullObject="fullObjects['licensePlate']"
            :value="formValues.licensePlate"
            @update:formValues="updateFormValues({ key: 'licensePlate', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'licensePlate', value: $event })"
          />
        </div>
        <div class="mc-modal__filter-item" data-me="master-fr-modal-filter-driver">
          <FormulateInput
            v-bind="uGroupFields"
            :fullObject="fullObjects['uGroup']"
            :value="formValues.uGroup"
            @update:formValues="updateFormValues({ key: 'uGroup', value: $event })"
            @update:fullObject="updateFullObjects({ key: 'uGroup', value: $event })"
          />
        </div>
        <div class="mc-modal__filter-item" data-me="master-fr-modal-filter-face-recognition">
          <FormulateInput
            type="group"
            name="faceRecognition"
            :label="$t('rfidAndFacerecognition.filter.faceRecognition.label')"
            :value="(formValues.faceRecognition = faceRecognitionCheck)"
          >
            <div class="mc-modal__filter-button-group">
              <button
                v-for="option in faceRecognitionOptions"
                :key="option.key"
                :data-me="`master-fr-modal-filter-face-recognition-btn-${option.key}`"
                type="button"
                :class="{ 'mc-modal__filter-button--active': faceRecognitionCheck[option.key] }"
                class="mc-modal__filter-button"
                @click="filterFaceRecognition(option.key)"
              >
                {{ option.label }}
              </button>
            </div>
          </FormulateInput>
        </div>
        <div class="mc-modal__filter-item" data-me="master-fr-modal-filter-rfid">
          <FormulateInput
            type="group"
            name="rfid"
            :label="$t('rfidAndFacerecognition.filter.rfid.label')"
            :value="(formValues.rfid = rfidCheck)"
          >
            <div class="mc-modal__filter-button-group">
              <button
                v-for="option in rfidOptions"
                :key="option.key"
                :data-me="`master-fr-modal-filter-rfid-btn-${option.key}`"
                type="button"
                :class="{ 'mc-modal__filter-button--active': rfidCheck[option.key] }"
                class="mc-modal__filter-button"
                @click="filterRfid(option.key)"
              >
                {{ option.label }}
              </button>
            </div>
          </FormulateInput>
        </div>
        <div class="mc-modal__filter-item" data-me="master-fr-modal-filter-configuration-status">
          <FormulateInput
            type="group"
            name="configurationStatus"
            :label="$t('rfidAndFacerecognition.filter.configurationStatus.label')"
            :value="(formValues.configurationStatus = configurationStatusCheck)"
          >
            <div class="mc-modal__filter-button-group">
              <button
                v-for="option in configurationStatusOptions"
                :key="option.key"
                :data-me="`master-fr-modal-filter-configuration-status-btn-${option.key}`"
                type="button"
                :class="{
                  'mc-modal__filter-button--active': configurationStatusCheck[option.key],
                }"
                class="mc-modal__filter-button"
                @click="filterConfigurationStatus(option.key)"
              >
                {{ option.label }}
              </button>
            </div>
          </FormulateInput>
        </div>
      </template>
    </McTableFilter>
  </div>
</template>

<script>
import McTableFilter from '@/components/McTableFilter'
import McCheckbox from '@/components/McCheckbox'
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'
import { MixPanel } from '@/mixin/mixpanel'
import { GET_VEHICLE_FILTER } from '@/services/api/master/fleet'
import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'

const INITIAL_FACE_RECOGNITION_CHECK = {
  all: true,
  V: false,
  N: false,
  P: false,
  I: false,
  E: false,
}
const INITIAL_RFID_CHECK = { all: true, S: false, N: false, P: false, E: false }
const INITIAL_CONFIGURATION_STATUS_CHECK = { all: true, A: false, S: false, N: false }

export default {
  components: {
    McTableFilter,
    McCheckbox,
  },
  props: {
    activeFilters: {
      type: Object,
      required: true,
    },
  },
  mixins: [MixPanel],
  emits: ['input', 'update:fullObject', 'applyFilters'],
  data() {
    return {
      phaseOneShow: false,
      localFilters: {},
      savedFullObjects: {},
      faceRecognitionCheck: { ...INITIAL_FACE_RECOGNITION_CHECK },
      rfidCheck: { ...INITIAL_RFID_CHECK },
      configurationStatusCheck: { ...INITIAL_CONFIGURATION_STATUS_CHECK },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    parseFilterDataMappings() {
      return {
        faceRecognition: 'faceRecognition',
        rfid: 'rfid',
        configurationStatus: 'configurationStatus',
        licensePlate: 'licensePlate',
        uGroup: 'uGroup',
      }
    },
    faceRecognitionOptions() {
      return [
        { key: 'all', label: this.$t('global.filter.all') },
        { key: 'E', label: this.$t('rfidAndFacerecognition.filter.faceRecognition.withPhoto') },
        { key: 'N', label: this.$t('rfidAndFacerecognition.filter.faceRecognition.withoutPhoto') },
        { key: 'P', label: this.$t('rfidAndFacerecognition.filter.faceRecognition.proccessing') },
        { key: 'V', label: this.$t('rfidAndFacerecognition.filter.faceRecognition.validPhoto') },
        { key: 'I', label: this.$t('rfidAndFacerecognition.filter.faceRecognition.invalidPhoto') },
      ]
    },
    rfidOptions() {
      return [
        { key: 'all', label: this.$t('global.filter.all') },
        { key: 'E', label: this.$t('rfidAndFacerecognition.filter.rfid.withRfid') },
        { key: 'N', label: this.$t('rfidAndFacerecognition.filter.rfid.withoutRfid') },
        { key: 'P', label: this.$t('rfidAndFacerecognition.filter.rfid.proccessing') },
      ]
    },
    configurationStatusOptions() {
      return [
        { key: 'all', label: this.$t('global.filter.all') },
        {
          key: 'A',
          label: this.$t('rfidAndFacerecognition.filter.configurationStatus.fullyInstalled'),
        },
        {
          key: 'S',
          label: this.$t('rfidAndFacerecognition.filter.configurationStatus.partiallyInstalled'),
        },
        {
          key: 'N',
          label: this.$t('rfidAndFacerecognition.filter.configurationStatus.notInstalled'),
        },
      ]
    },
    vehicleFields() {
      return {
        name: 'licensePlate',
        isShow: true,
        label: this.$t('report.fields.licensePlate'),
        type: 'report-filter-value-help',
        placeholder: `${this.$t('global.action.choose')} ${this.$t('report.fields.licensePlate')}`,
        trackBy: 'id',
        displayedBy: 'licensePlate',
        listGetter: (filter) => this.getVehicleList(filter),
        multiple: true,
        tableFields: [
          {
            label: this.$t('report.fields.licensePlate'),
            key: 'licensePlate',
            isSortable: true,
          },
          {
            label: this.$t('report.fields.vehicleType'),
            key: 'vehicleType',
            isSortable: false,
            formatter: (value) =>
              value
                ? `${value.manufacturer ?? '-'} / ${value.model ?? '-'} / ${value.type ?? '-'} `
                : '-',
          },
          {
            label: this.$t('report.fields.vehicleGroup'),
            key: 'vehicle2Vgroup',
            isSortable: false,
            formatter: (value) =>
              value?.length > 0 ? value.map((v) => v.vgroup?.name ?? '-').join(', ') : '-',
          },
        ],
      }
    },
    uGroupFields() {
      return {
        name: 'uGroup',
        isShow: true,
        label: this.$t('rfidAndFacerecognition.fieldsHeader.groupUser'),
        type: 'value-help-v2',
        placeholder: `${this.$t('global.action.choose')} ${this.$t(
          'rfidAndFacerecognition.fieldsHeader.groupUser'
        )}`,
        trackBy: 'id',
        displayedBy: 'name',
        listGetter: (filter) => this.getUGroupList(filter),
        multiple: true,
        tableFields: [
          {
            label: this.$t('rfidAndFacerecognition.fieldsHeader.name'),
            key: 'name',
            isSortable: true,
          },
        ],
      }
    },
  },
  watch: {
    activeFilters: {
      handler(newVal) {
        if (!newVal) return
        this.setLocalFilters(newVal)
      },
      deep: true,
    },
  },
  methods: {
    show() {
      this.$refs.filterRef.showModal = true
    },
    hide() {
      this.$refs.filterRef.showModal = false
    },
    setLocalFilters(newVal) {
      this.localFilters = cloneDeep(newVal)
    },

    filterFaceRecognition(key) {
      this.handleSelectFilter('faceRecognitionCheck', key)
    },
    filterRfid(key) {
      this.handleSelectFilter('rfidCheck', key)
    },
    filterConfigurationStatus(key) {
      this.handleSelectFilter('configurationStatusCheck', key)
    },

    handleSelectFilter(stateObjName, key) {
      const source = this[stateObjName]
      if (!source || typeof source !== 'object' || Array.isArray(source)) return

      const current = { ...source }

      if (key === 'all') {
        Object.keys(current).forEach((k) => {
          current[k] = k === 'all'
        })
      } else {
        current[key] = !current[key]
        current.all = false
        const hasActive = Object.keys(current).some((k) => k !== 'all' && current[k])
        if (!hasActive) current.all = true
      }

      this[stateObjName] = current
    },

    handleReset() {
      this.faceRecognitionCheck = { ...INITIAL_FACE_RECOGNITION_CHECK }
      this.rfidCheck = { ...INITIAL_RFID_CHECK }
      this.configurationStatusCheck = { ...INITIAL_CONFIGURATION_STATUS_CHECK }
      this.savedFullObjects = {}
    },

    handleSubmit(value) {
      if (!value) return

      const filterMap = {
        faceRecognition: this.faceRecognitionCheck,
        rfid: this.rfidCheck,
        configurationStatus: this.configurationStatusCheck,
      }

      value.forEach((item) => {
        if (filterMap[item.key]) {
          item.values = Object.keys(filterMap[item.key]).filter(
            (k) => filterMap[item.key][k] && k !== 'all'
          )
        }

        if (item.key === 'licensePlate') {
          this.savedFullObjects.licensePlate = this.$refs.filterRef.fullObjects?.licensePlate || []
        }
        if (item.key === 'uGroup') {
          this.savedFullObjects.uGroup = this.$refs.filterRef.fullObjects?.uGroup || []
        }
      })

      this.$emit('applyFilters', value)
    },

    async getVehicleList(opt) {
      const { orderBy, ...otherOpt } = opt
      const response = await this.$apollo.query({
        query: GET_VEHICLE_FILTER,
        variables: {
          filter: {
            status: ['I', 'R', 'A'],
            orderBy: orderBy ?? JSON.stringify({ licensePlate: 'asc' }),
            ...otherOpt,
          },
        },
      })

      const statusLabelMap = {
        I: this.$t('master.status.aktif'),
        R: this.$t('master.status.perbaikan'),
      }

      const res = { ...response.data.vehiclesFilter.vehiclesFilterPaginated }
      res.data = res.data.map((item) => ({
        ...item,
        statusString: statusLabelMap[item.status] ?? this.$t('master.status.arsip'),
      }))
      return res
    },
    async getUGroupList(opts) {
      let result = {}
      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.limit,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        query: GET_USER_GROUP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
          },
        },
      })

      const ugroups = res.data.ugroups.ugroups

      result = {
        metadata: {
          total_count: ugroups.pagination.total,
          page: ugroups.pagination.page,
        },
        data: {
          paginated_result: ugroups.data,
        },
      }

      return result
    },
  },
}
</script>
